import React, { useState, useEffect } from 'react';
import { fairManagerApi } from '../services/api';
import { Fair } from '../types';
import {
  TrendingUp,
  Calendar,
  Users,
  Package,
  AlertCircle
} from 'lucide-react';
import { extractApiError } from '../utils/errorUtils';
import { formatDateRange, parseDateOnly } from '../utils/fairStatus'; // Issue #1672

export default function FairManagerStatistics() {
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await fairManagerApi.getFairs();
      setFairs(data);
      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load statistics:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics from fairs data
  const totalFairs = fairs.length;
  const upcomingFairs = fairs.filter(fair => {
    const start = parseDateOnly(fair.start_date);
    return start ? start > new Date() : false;
  }).length;
  const pastFairs = totalFairs - upcomingFairs;
  const totalSlots = fairs.reduce((sum, fair) => sum + (fair.total_slots || 0), 0);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Statistics Overview</h1>
        <div className="text-center py-12">
          <p className="text-gray-500">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Statistics Overview</h1>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={loadStats}
            className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (totalFairs === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Statistics Overview</h1>
        <div className="text-center py-12">
          <TrendingUp className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">No Statistics Yet</h2>
          <p className="text-gray-500 mb-6">
            Statistics will appear once you create your first fair
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Statistics Overview</h1>
        <p className="text-gray-600">
          Overview of your managed fairs and key metrics
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        {/* Total Fairs */}
        <div className="bg-gradient-to-br from-teal-50 to-moonbone rounded-lg p-6 border border-teal-200" data-testid="stat-total-fairs">
          <div className="flex items-center justify-between mb-2">
            <div className="text-ink font-semibold">Total Fairs</div>
            <Calendar className="w-5 h-5 text-secondary" />
          </div>
          <div className="text-3xl font-bold text-ink" data-testid="stat-value">{totalFairs}</div>
          <div className="text-sm text-secondary mt-1">All managed events</div>
        </div>

        {/* Upcoming Fairs */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border border-green-200" data-testid="stat-upcoming-fairs">
          <div className="flex items-center justify-between mb-2">
            <div className="text-green-700 font-semibold">Upcoming</div>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-green-900" data-testid="stat-value">{upcomingFairs}</div>
          <div className="text-sm text-green-600 mt-1">Future events</div>
        </div>

        {/* Past Fairs */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200" data-testid="stat-past-fairs">
          <div className="flex items-center justify-between mb-2">
            <div className="text-purple-700 font-semibold">Completed</div>
            <Package className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-3xl font-bold text-purple-900" data-testid="stat-value">{pastFairs}</div>
          <div className="text-sm text-purple-600 mt-1">Past events</div>
        </div>

        {/* Total Slots */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6 border border-orange-200" data-testid="stat-total-slots">
          <div className="flex items-center justify-between mb-2">
            <div className="text-orange-700 font-semibold">Total Slots</div>
            <Users className="w-5 h-5 text-orange-600" />
          </div>
          <div className="text-3xl font-bold text-orange-900" data-testid="stat-value">{totalSlots}</div>
          <div className="text-sm text-orange-600 mt-1">Vendor booths</div>
        </div>
      </div>

      {/* Fairs List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Fairs</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {fairs.slice(0, 10).map((fair) => (
            <div key={fair.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{fair.name}</h3>
                  <div className="text-sm text-gray-600 mt-1">
                    {fair.city && fair.state && (
                      <span>{fair.city}, {fair.state}</span>
                    )}
                    {fair.start_date && fair.end_date && (
                      <span className="ml-3">
                        {formatDateRange(fair.start_date, fair.end_date)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {fair.total_slots !== undefined && (
                    <div className="text-sm font-semibold text-gray-900">
                      {fair.total_slots} slots
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Note about future enhancements */}
      <div className="mt-6 bg-moonbone border border-teal-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-secondary mt-0.5 mr-3" />
          <div className="text-sm text-ink">
            <strong>Coming Soon:</strong> More detailed statistics including revenue analytics,
            reservation metrics, vendor engagement, and waitlist conversion rates will be available in future updates.
          </div>
        </div>
      </div>
    </div>
  );
}
